import type { EventTuple } from '@/events/types'
import { createLogger } from '@/debug/logger'
import { encodeDevalue, decodeDevalue } from '@/persistence/devalueCodec'
import type { CheckpointBlob } from '@/persistence/checkpointCodec'
import { deleteThumbnailImage, getCachedThumbnailImage, setThumbnailImage } from '@/persistence/thumbnailStore'

// Types shared by persistence layers
export type DocumentMeta = {
  id: string
  name: string
  createdAt: string
  updatedAt: string
  // Durable commit ID at last save (ignores ephemeral drafts)
  commitId: number
}

export type SavedDocument = DocumentMeta & {
  // Tail events since the latest checkpoint
  events: EventTuple[]
  // Compressed checkpoint blob (base + meta)
  checkpoint: CheckpointBlob
  // Commit id of the baseline checkpoint represented by 'checkpoint'
  checkpointCommitId: number
  // Monotonic save operation revision, used only to reject async stale writes.
  saveRevision?: number
}

// Persisted namespace is intentionally left as "scenes" for compatibility with
// existing localStorage documents.
const NS = 'huebo:scenes'
const KEY_INDEX = `${NS}:index`
const THUMB_PREFIX = `${NS}:thumb:`
const THUMB_META_PREFIX = `${NS}:thumbmeta:`
const log = createLogger('persistence')

// Low-level storage helpers
function safeGet<T>(key: string): T | null {
  const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
  const raw = localStorage.getItem(key)
  if (!raw) return null
  const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
  const value = decodeDevalue<T>(raw)
  const t2 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
  if (log.debugEnabled()) {
    const kb = (raw.length / 1024).toFixed(2)
    log.debug('safeGet', { key, readMs: (t1 - t0).toFixed(2), parseMs: (t2 - t1).toFixed(2), sizeKb: kb })
  }
  return value
}

function storagePayloadSummary(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object') return { type: typeof value }
  const record = value as Record<string, unknown>
  return {
    keys: Object.keys(record),
    eventCount: Array.isArray(record.events) ? record.events.length : undefined,
    hasCheckpoint: Boolean(record.checkpoint),
    checkpointCommitId: record.checkpointCommitId,
    commitId: record.commitId,
  }
}

function safeSet(key: string, value: unknown) {
  try {
    const t0 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    const text = encodeDevalue(value)
    const t1 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    localStorage.setItem(key, text)
    const t2 = (typeof performance !== 'undefined' ? performance.now() : Date.now())
    if (log.debugEnabled()) {
      const kb = (text.length / 1024).toFixed(2)
      log.debug('safeSet', { key, devalueMs: (t1 - t0).toFixed(2), writeMs: (t2 - t1).toFixed(2), sizeKb: kb })
    }
  } catch (err) {
    log.error('safeSet failed', { key, err, payload: storagePayloadSummary(value) })
    throw err
  }
}

function documentKey(id: string) { return `${NS}:${id}` }

function getIndex(): DocumentMeta[] {
  return safeGet<DocumentMeta[]>(KEY_INDEX) || []
}

/**
 * Deduplicate documents by id, keeping the most recently updated entry.
 */
function deduplicateDocuments(documents: DocumentMeta[]): DocumentMeta[] {
  const byId = new Map<string, DocumentMeta>()
  for (const m of documents) {
    const prev = byId.get(m.id)
    if (!prev || prev.updatedAt < m.updatedAt) {
      byId.set(m.id, m)
    }
  }
  return Array.from(byId.values())
}

function putIndex(index: DocumentMeta[]) {
  safeSet(KEY_INDEX, index)
  notifyDocumentsChanged()
}

export function upsertIndex(meta: DocumentMeta) {
  const idx = getIndex()
  const i = idx.findIndex((m) => m.id === meta.id)
  if (i >= 0) idx[i] = meta
  else idx.push(meta)
  putIndex(idx)
}

export function listDocuments(): DocumentMeta[] {
  const idx = getIndex()
  const deduplicated = deduplicateDocuments(idx)
  const normalized: DocumentMeta[] = []
  for (const meta of deduplicated) {
    const record = readDocument(meta.id)
    if (!record) {
      if (log.warnEnabled()) log.warn('index entry missing document record', { id: meta.id })
      normalized.push(meta)
      continue
    }
    normalized.push({
      id: meta.id,
      name: record.name || meta.name || 'Untitled',
      createdAt: record.createdAt || meta.createdAt,
      updatedAt: record.updatedAt || meta.updatedAt,
      commitId: record.commitId ?? meta.commitId ?? 0,
    })
  }
  const dedupedNormalized = deduplicateDocuments(normalized)
  return dedupedNormalized.sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
}

export function readDocument(id: string): SavedDocument | null {
  const payload = safeGet<Record<string, unknown>>(documentKey(id))
  if (!payload) return null
  const {
    name,
    createdAt,
    updatedAt,
    events,
    checkpoint,
    commitId,
    checkpointCommitId,
    saveRevision,
  } = payload || {}
  if (!Array.isArray(events)) return null
  if (!checkpoint) {
    log.warn('readDocument missing checkpoint', { id })
    return null
  }
  const durableCommitId = typeof commitId === 'number' && commitId >= 0 ? commitId : events.length
  const baselineCommitId =
    typeof checkpointCommitId === 'number' && checkpointCommitId >= 0
      ? checkpointCommitId
      : Math.max(0, durableCommitId - events.length)
  return {
    id,
    name: typeof name === 'string' && name ? name : 'Untitled',
    createdAt: typeof createdAt === 'string' ? createdAt : '',
    updatedAt: typeof updatedAt === 'string' ? updatedAt : '',
    events: events as EventTuple[],
    checkpoint: checkpoint as SavedDocument['checkpoint'],
    checkpointCommitId: baselineCommitId,
    commitId: durableCommitId,
    saveRevision: isFiniteRevision(saveRevision) ? saveRevision : undefined,
  }
}

function isFiniteRevision(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
}

function isStaleDocumentRecord(incoming: SavedDocument, existing: SavedDocument): boolean {
  if (isFiniteRevision(incoming.saveRevision) && isFiniteRevision(existing.saveRevision)) {
    return incoming.saveRevision < existing.saveRevision
  }

  // Records written before saveRevision existed can only be compared by their
  // durable state. Allow newer revision-bearing writes through so undo can
  // persist an earlier history pointer over a legacy record.
  if (isFiniteRevision(incoming.saveRevision) && !isFiniteRevision(existing.saveRevision)) {
    return false
  }

  if (!isFiniteRevision(incoming.saveRevision) && isFiniteRevision(existing.saveRevision)) {
    return incoming.commitId < existing.commitId
  }

  if (incoming.commitId < existing.commitId) return true
  if (
    incoming.commitId === existing.commitId
    && incoming.events.length < existing.events.length
    && incoming.checkpointCommitId <= existing.checkpointCommitId
  ) {
    return true
  }
  return false
}

/**
 * Documents thrown away this session (an empty world left behind). A save still in flight when the
 * world was thrown away (an async checkpoint encode, the unmount's tail save) must not bring it back.
 */
const discardedDocumentIds = new Set<string>()

/** Delete a document and refuse any later write to it in this session. */
export function discardDocument(id: string): void {
  discardedDocumentIds.add(id)
  deleteDocument(id)
}

export function writeDocumentRecord(id: string, record: SavedDocument): boolean {
  if (discardedDocumentIds.has(id)) return false
  if (!record.checkpoint) {
    throw new Error(`writeDocumentRecord missing checkpoint for ${id}`)
  }
  if (typeof record.checkpointCommitId !== 'number' || record.checkpointCommitId < 0) {
    throw new Error(`writeDocumentRecord missing checkpointCommitId for ${id}`)
  }
  const existing = readDocument(id)
  if (existing) {
    if (isStaleDocumentRecord(record, existing)) {
      log.warn('writeDocumentRecord discarded stale snapshot', {
        id,
        existingCommit: existing.commitId,
        incomingCommit: record.commitId,
        existingSaveRevision: existing.saveRevision,
        incomingSaveRevision: record.saveRevision,
      })
      return false
    }
  }
  safeSet(documentKey(id), record)
  return true
}

export function deleteDocument(id: string) {
  localStorage.removeItem(documentKey(id))
  localStorage.removeItem(`${THUMB_PREFIX}${id}`)
  localStorage.removeItem(`${THUMB_META_PREFIX}${id}`)
  deleteThumbnailImage(id).catch((err) => {
    log.warn('failed to delete IndexedDB thumbnail', { id, err })
  })
  const idx = getIndex().filter((m) => m.id !== id)
  putIndex(idx)
}

export function getDocumentThumbnail(id: string): string | null {
  return getCachedThumbnailImage(id)
}

export function setDocumentThumbnail(id: string, dataUrl: string) {
  setThumbnailImage(id, dataUrl).catch((err) => {
    log.warn('failed to persist IndexedDB thumbnail', { id, err })
  })
}

export type ThumbnailMeta = {
  commitId: number
  renderVersion?: string
  width?: number
  height?: number
}

export function getDocumentThumbnailMeta(id: string): ThumbnailMeta | null {
  return safeGet<ThumbnailMeta>(`${THUMB_META_PREFIX}${id}`)
}

export function setDocumentThumbnailMeta(id: string, meta: ThumbnailMeta) {
  safeSet(`${THUMB_META_PREFIX}${id}`, meta)
}

export function isThumbnailStale(document: DocumentMeta): boolean {
  const meta = getDocumentThumbnailMeta(document.id)
  if (!meta) return true
  return document.commitId !== meta.commitId
}

// --- Lightweight subscription for document index changes
let documentIndexSubs = new Set<() => void>()
let storageHooked = false
function hookStorage() {
  if (storageHooked) return
  storageHooked = true
  if (typeof window === 'undefined') return
  window.addEventListener('storage', (e) => {
    if (!e.key) return
    if (e.key === KEY_INDEX || e.key.startsWith(NS + ':')) {
      notifyDocumentsChanged()
    }
  })
}

function notifyDocumentsChanged() {
  const run = () => {
    documentIndexSubs.forEach((fn) => { fn() })
  }
  if (typeof queueMicrotask === 'function') queueMicrotask(run)
  else setTimeout(run, 0)
}

export function subscribeDocumentsIndex(cb: () => void): () => void {
  documentIndexSubs.add(cb)
  hookStorage()
  return () => { documentIndexSubs.delete(cb) }
}
